import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import TableCell from '@mui/material/TableCell';
import DialogConfirm from '../../../components/_dialog/DialogConfirm';
import ExternalLinkIconButton from '../../../components/_iconButton/ExternalLinkIconButton';
import CopyIconButton from '../../../components/_iconButton/CopyIconButton';
import { useDeleteCompanyKeywordClassificationsMutation } from '../../../../graphql/mutation/companyKeywordClassification/deleteCompanyKeywordClassificationsMutation';
import useNotification from '../../../components/_hook/useNotification';
import { useUnpublishCompanyKeywordClassificationsMutation } from '../../../../graphql/mutation/companyKeywordClassification/unpublishCompanyKeywordClassificationsMutation';
import StatusIconButton from '../../../components/_iconButton/StatusIconButton';
import { usePublishCompanyKeywordClassificationsMutation } from '../../../../graphql/mutation/companyKeywordClassification/publishCompanyKeywordClassificationsMutation';
import DeleteIconButton from '../../../components/_iconButton/DeleteIconButton';
import useKeepitalRoutes from '../../../components/_hook/useKeepitalRoutes';
import { ICompanyKeywordClassificationSchema } from '../../../../graphql/type/companyKeywordClassification';
import Context from '../../../context';

interface IProps {
  item: ICompanyKeywordClassificationSchema;
  setTableRefreshKey?: (tableRefreshKey: number) => void;
}

export default function BusinessClassificationTableItem(props: IProps) {
  const { t } = useTranslation();
  const { item, setTableRefreshKey } = props;
  const { notification } = useNotification();
  const { path: keepitalPath } = useKeepitalRoutes();
  const { company } = useContext(Context);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [
    deleteCompanyKeywordClassificationsMutation,
    { loading: isDeletingCompanyKeywordClassifications },
  ] = useDeleteCompanyKeywordClassificationsMutation('isSuccess', {
    onCompleted: () => {
      notification.success(
        t('__item___deleted', { item: `${item._id} ${t('classification')}` })
      );
      if (setTableRefreshKey) {
        setTableRefreshKey(new Date().getTime());
      }
    },
    onError: (e) => {
      notification.error(e.message);
    },
  });
  const [
    publishCompanyKeywordClassificationsMutation,
    { loading: isPublishingCompanyKeywordClassifications },
  ] = usePublishCompanyKeywordClassificationsMutation(
    `updatedCompanyKeywordClassifications {
				_id
				id
				status
			}`,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [
    unpublishCompanyKeywordClassificationsMutation,
    { loading: isUnpublishingCompanyKeywordClassifications },
  ] = useUnpublishCompanyKeywordClassificationsMutation(
    `updatedCompanyKeywordClassifications {
				_id
				id
				status
			}`,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function onClickPublish() {
    if (item._id) {
      await publishCompanyKeywordClassificationsMutation({
        variables: {
          companyId: company._id,
          classificationIds: [item.id],
        },
      });
    }
  }

  async function onClickUnpublish() {
    if (item._id) {
      await unpublishCompanyKeywordClassificationsMutation({
        variables: {
          companyId: company._id,
          classificationIds: [item.id],
        },
      });
    }
  }

  async function onClickDelete() {
    if (item._id) {
      await deleteCompanyKeywordClassificationsMutation({
        variables: {
          companyId: company._id,
          classificationIds: [item.id],
        },
      });
    }
  }

  return (
    <>
      <DialogConfirm
        title={t('are_you_sure_you_want_to_delete')}
        open={isOpenDeleteDialog}
        onClose={() => setIsOpenDeleteDialog(false)}
        onConfirm={onClickDelete}
      />
      <TableCell align="left">
        <Tooltip title={item._id}>
          <span>{item._id.substr(0, 6)}</span>
        </Tooltip>
        <CopyIconButton iconHtmlFontSize="0.8rem" copyText={item._id} />
      </TableCell>
      <TableCell align="left">
        {item.classificationData ? item.classificationData.name : '-'}
      </TableCell>
      <TableCell align="center">
        <span className="text-muted">
          {item.classificationData
            ? item.classificationData.la.toUpperCase()
            : '-'}
        </span>
      </TableCell>
      <TableCell align="right">
        <StatusIconButton
          status={item.status}
          loading={
            isUnpublishingCompanyKeywordClassifications ||
            isPublishingCompanyKeywordClassifications
          }
          tooltip={item.status ? t('publish') : t('unpublish')}
          onClick={item.status ? onClickUnpublish : onClickPublish}
        />
        <DeleteIconButton
          loading={isDeletingCompanyKeywordClassifications}
          tooltip={t('delete')}
          onClick={() => setIsOpenDeleteDialog(true)}
        />
        {item.classificationData && (
          <ExternalLinkIconButton
            tooltip={`${t('classification')} ${t('page')}`}
            href={keepitalPath('classification.index', {
              slug: item.classificationData.slug,
            })}
            target="_blank"
          />
        )}
      </TableCell>
    </>
  );
}
